import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import qs from 'querystring';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import matchSorter from 'match-sorter';
import * as propertyActions from '../../../../actions/properties';
import * as accommodationActions from '../../../../actions/accommodations';
import * as sidebarActions from '../../../../actions/sidebar';
import Layout from '../../../../layout';
import SearchBar from '../../../../components/common/SearchBar';
import Input from '../../../../components/common/Input';
import {
  Tabs,
  Tab,
  TabList,
  TabPill,
  TabPanel
} from '../../../../components/common/Tabs';
import CheckboxList from '../../../../components/common/List/CheckboxList';
import { List, ListItem } from '../../../../components/common/List';

const propTypes = {
  hotel: PropTypes.object.isRequired,
  hotels: PropTypes.array.isRequired,
  accommodations: PropTypes.array.isRequired,
  fetchProperty: PropTypes.func.isRequired,
  fetchProperties: PropTypes.func.isRequired,
  fetchAccommodations: PropTypes.func.isRequired,
  updateSidebar: PropTypes.func.isRequired,
  cloneAccommodations: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

/**
 * singularize is a would-be singularize function to strip of the last s. This
 * won't go very far...
 *
 * @param {String} str
 * @returns {String}
 * @api private
 */

const singularize = str => {
  return str.replace('-', '').replace(/s$/, '');
};

/**
 * getTypeLable returns the human readable format for the type value
 *
 * @param {String} v
 * @returns {String}
 * @api private
 */

const getTypeLabel = v => {
  return {
    'guest-rooms': 'Guest Rooms',
    suites: 'Suite',
    accessible: 'Accessible',
    miscs: 'Misc'
  }[v];
};

const Clone = ({
  hotel,
  hotels,
  accommodations,
  fetchProperties,
  fetchProperty,
  fetchAccommodations,
  updateSidebar,
  cloneAccommodations,
  match: {
    url,
    params: { hotelid, type }
  },
  location: { search },
  history
}) => {
  const [searchAccommodations, setSearchAccommodations] = useState('');
  const [searchHotels, setSearchHotels] = useState('');
  const [checked, setChecked] = useState({});
  const [accommodationsChecked, setAccommodationsChecked] = useState({});
  useEffect(() => {
    const { selected } = qs.parse(search.slice(1));
    if (selected) {
      setAccommodationsChecked({
        ...accommodationsChecked,
        [selected]: !accommodationsChecked[selected]
      });
      history.replace(url);
    }
    fetchProperties({ type: 'hotel', per_page: 300 });
    fetchProperty(hotelid).then(property => {
      updateSidebar({ titles: { Hotel: property.name } });
    });
    fetchAccommodations(hotelid, { type: singularize(type), per_page: 1000 });
    return () => {};
  }, []);

  const onSubmit = evt => {
    evt.preventDefault();

    const ids = Object.keys(accommodationsChecked)
      .map(v => {
        return accommodationsChecked[v] ? v : undefined;
      })
      .filter(v => v);

    const toPropertyIds = Object.keys(checked)
      .map(v => {
        return checked[v] ? v : undefined;
      })
      .filter(v => v);

    if (!ids.length || !toPropertyIds.length) {
      // TODO alert or notification that both items and properties to clone to
      // must have at least one item checkd
      return;
    }

    cloneAccommodations(hotelid, ids, toPropertyIds).then(() => {
      history.push(`/hotels/${hotelid}/accommodations/${type}`);
    });
  };

  const filteredAccommodations = matchSorter(
    accommodations,
    searchAccommodations,
    {
      keys: ['title']
    }
  );
  const currentAccommodations = filteredAccommodations.filter(
    accommodation => !accommodation.archived
  );
  const archivedAccommodations = filteredAccommodations.filter(
    accommodation => accommodation.archived
  );
  const filteredHotels = matchSorter(hotels, searchHotels, { keys: ['name'] });

  const typeLabel = getTypeLabel(type);

  return (
    <Layout metaTitle="Clone a Accommodation">
      <div className="w-full max-w-3xl">
        <Link
          to={`/hotels/${hotelid}/accommodations/guest-rooms`}
          className="mb-8 inline-block text-sm text-grey-dark hover:text-grey-darkest no-underline"
        >
          &lt; Back to {typeLabel}
        </Link>
        <h1 className="mb-6">Clone a Accommodation</h1>
        <div className="mb-16">
          Use this page to Clone Accommodations on {hotel.name}.
        </div>
        <div className="flex flex-wrap justify-between mb-16">
          <div style={{ width: 440 }}>
            <SearchBar
              id="filter-accommodations"
              className="mb-4"
              placeholder="Filter Accommodations..."
              value={searchAccommodations}
              onChange={({ value }) => setSearchAccommodations(value)}
            />
            <Tabs>
              <TabList>
                <Tab>
                  <span className="mr-2">Current</span>
                  <TabPill id="current-pill">
                    {currentAccommodations.length}
                  </TabPill>
                </Tab>
                <Tab>
                  <span className="mr-2">Archived</span>
                  <TabPill id="archived-pill">
                    {archivedAccommodations.length}
                  </TabPill>
                </Tab>
              </TabList>

              <TabPanel>
                <List id="current-list">
                  {currentAccommodations.map(accommodation => (
                    <ListItem id={accommodation.id} key={accommodation.id}>
                      <Input
                        id={accommodation.id}
                        type="checkbox"
                        className="mr-4"
                        input={{
                          checked: !!accommodationsChecked[accommodation.id],
                          onChange: () =>
                            setAccommodationsChecked({
                              ...accommodationsChecked,
                              [accommodation.id]: !accommodationsChecked[
                                accommodation.id
                              ]
                            })
                        }}
                      />
                      <Link
                        to={`/hotels/${hotelid}/accommodations/${type}/${
                          accommodation.id
                        }/edit`}
                      >
                        {accommodation.title}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>
              <TabPanel>
                <List id="archived-list">
                  {archivedAccommodations.map(accommodation => (
                    <ListItem id={accommodation.id} key={accommodation.id}>
                      <Input
                        id={accommodation.id}
                        type="checkbox"
                        className="mr-4"
                        input={{
                          checked: !!accommodationsChecked[accommodation.id],
                          onChange: () =>
                            setAccommodationsChecked({
                              ...accommodationsChecked,
                              [accommodation.id]: !accommodationsChecked[
                                accommodation.id
                              ]
                            })
                        }}
                      />
                      <Link
                        to={`/hotels/${hotelid}/accommodations/${type}/${
                          accommodation.id
                        }/edit`}
                      >
                        {accommodation.title}
                      </Link>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>
            </Tabs>
          </div>
          <div style={{ width: 440 }}>
            <SearchBar
              id="filter-hotels"
              className="mb-16"
              placeholder="Filter Hotels..."
              value={searchHotels}
              onChange={({ value }) => setSearchHotels(value)}
            />
            <CheckboxList
              id="checkbox-hotels"
              title="Hotels"
              data={filteredHotels.map(hotelItem => ({
                id: hotelItem.id,
                name: hotelItem.name,
                displayName: item => (
                  <Link to={`/hotels/${item.id}`} className="mr-4">
                    {item.name}
                  </Link>
                ),
                checked: checked[hotelItem.id],
                onChange: () => {
                  setChecked({
                    ...checked,
                    [hotelItem.id]: !checked[hotelItem.id]
                  });
                }
              }))}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" className="btn" onClick={onSubmit}>
            Clone
          </button>
        </div>
      </div>
    </Layout>
  );
};

Clone.propTypes = propTypes;

export default connect(
  (state, props) => ({
    hotel: state.property.data,
    hotels: state.properties.data,
    accommodations: state.accommodations.data,
    isLoading: state.property.isLoading || state.accommodations.isLoading
  }),
  { ...propertyActions, ...accommodationActions, ...sidebarActions }
)(Clone);
